/** @jsx jsx */

import * as React from "react"
import { css, jsx } from "@emotion/core";
import * as _ from "lodash"
import { connect } from "react-redux"
import { IAppointmentScheduleResponse, IConsultationRoom } from "@curefit/care-common";
import AppUtils, { EpochConstants, LOADING_STATE } from "../utils/AppUtils"
import { AppConstants, ConsultationStatusConstant, ORION_IMG_URL, SUPPORTED_DOCTOR_TYPES_FOR_PACK_DETAILS } from "../utils/AppConstants";
import IDoctorProfile from "../models/IDoctorProfile"
import { IAppState } from "../models/IAppState"
import { AppointmentsActions } from "../actions/AppointmentsActions"
import { IHourInfo } from "../models/util/IHourInfo"
import { IDayEpochInfo } from "../models/util/IDayEpochInfo"
import ToggleButtonGroup, { IToggleButtonInfo } from "../components/utils/ToggleButtonGroup"
import DashboardCalendarComponent from "../components/DashboardCalendarComponent";
import ICenterInfo from "../models/ICenterInfo";
import IAccountData from "../models/IAccountData";
import { OngoingConsultationActions } from "../actions/OngoingConsultationActions";
import { Tracker } from "../utils/Tracker";
import CopyTextCont from "../components/common/CopyText";
import LocalStorageUtils from "../utils/LocalStorageUtils";
import { EventNames } from "../utils/EventTrackerConstants";
import CTA, { CTATheme } from "../components/common/CallToAction"
import ConsultationUtils from "../utils/ConsultationUtils";
import { shareProfileSytyles } from "../styles/components/ShareProfile";

export interface DashboardPageProps {
    accountData: IAccountData,
    doctorProfile: IDoctorProfile,
    appointments: IAppointmentScheduleResponse[],
    centerInfo: ICenterInfo,
    fetchDoctorAppointments: (doctorId: number, startTimeEpoch: number, numOfDays: number, isForPoll?: boolean, tillTimeEpoch?: number, isPackInfoRequired?: boolean) => Promise<IAppointmentScheduleResponse>,
    fetchCenterAppointments: (centerId: number, startTimeEpoch: number, numOfDays: number, isPackInfoRequired?: boolean) => Promise<IAppointmentScheduleResponse>,
    fetchRoomsInCenter: (centerId: number) => Promise<IConsultationRoom>,
    fetchDoctorsInCenter: (centerId: number) => Promise<IDoctorProfile>,
    clearOngoingConsultation: () => void
}

export interface IDaysBracket {
    startTimeEpoch: number,
    numberOfDays: number
}

export interface IDashboardUIParams {
    selectedColumnIdx: number;
}

export interface ITableParams {
    startColumn: number,
    numOfColumns: number,
}

export interface IDashboardDayInfo {
    dayEpochInfo: IDayEpochInfo,
    appointments: IAppointmentScheduleResponse[]
}

export interface IColumnInfo {
    appointments: IAppointmentScheduleResponse[],

}

export interface DashboardPageState {
    view: string,
    selectedDashboardView: string,
    tableParams: ITableParams,
    tableColumns: any[],
    uiParams: IDashboardUIParams,
    appointmentLoadingState: LOADING_STATE,
    toggleButtonInfos: IToggleButtonInfo[],
    hourSplit: IHourInfo[],
    daysBracket: IDaysBracket,
    doctorAppointmentsLoader: boolean
}

interface ShareProfileProps {
    doctorProfile: IDoctorProfile
}

const ShareProfile = (props: ShareProfileProps) => {
    const [popupStatus, togglePopup] = React.useState(false)
    const slugValue: string = AppUtils.getSlugUrl(props.doctorProfile);
    const [copyStatus, toggleCopyStatus] = React.useState(false)
    const copiedCls = copyStatus
        ? "enable"
        : ""
    const handleCopyClick = () => {
        if (!copyStatus) {
            LocalStorageUtils.setClickEvents({ flow: EventNames.Calendar, field: "Copy", section: EventNames.Share_Profile, widget: EventNames.Share_Profile })
            toggleCopyStatus(true)
            setTimeout(() => {
                toggleCopyStatus(false)
            }, 4000)
        }
    }
    const handleTogglePopup = () => {
        if (!popupStatus) {
            LocalStorageUtils.setClickEvents({ flow: EventNames.Calendar, field: EventNames.Share_Profile, section: EventNames.Share_Profile, widget: EventNames.Share_Profile })
        } else {
            LocalStorageUtils.setClickEvents({ flow: EventNames.Calendar, field: "Close", section: EventNames.Share_Profile, widget: EventNames.Share_Profile })
        }
        togglePopup(!popupStatus)
    }
    if (_.isEmpty(slugValue)) return null

    const isMobile = AppUtils.getIsMobile();
    return (
        <div className={"share-profile"}>
            {!isMobile &&
                // <CTA innerText={"Share profile"} theme={CTATheme.PRIMARY} onClick={handleTogglePopup} customStyle={{ width: 140, marginRight: 20 }} />

                <button onClick={handleTogglePopup} css={shareProfileSytyles.shareButton}>
                    <img src="/images/icons/share-profile.svg" css={shareProfileSytyles.shareIcon}></img>
                    <span>Share Profile</span>
                </button>
            }
            {popupStatus && <div className={"copy-profile-container"}>
                <img src="/images/close.svg" onClick={handleTogglePopup} />
                <div className={"share-header brandon-bold"}>Help patients book appointments</div>
                <div className={"detail"}>Share your profile on Care.fit with patients and help them conveniently book appointments with you.</div>
                <span className={"copy-feedback " + copiedCls}>Copied to clipboard</span>
                <CopyTextCont textToCopy={slugValue} copyCallback={handleCopyClick} />
            </div>}
        </div>
    )
}

class DashboardPage extends React.Component<DashboardPageProps, DashboardPageState> {
    private intervalFunction: any;
    private loadStartTime: number = AppUtils.getCurrentTime()
    constructor(props: DashboardPageProps) {
        super(props);
        let initColumnParams: ITableParams = {
            startColumn: AppUtils.getTodaysEpoch().startEpoch,
            numOfColumns: AppConstants.DAYS_IN_A_WEEK
        };
        let isDoctor = AppUtils.isDoctor(props.accountData);
        this.state = {
            view: isDoctor ? "doctor" : "nurse",
            selectedDashboardView: isDoctor ? "" : "room",
            uiParams: { selectedColumnIdx: 0 },
            tableParams: this.getInitTableParams(props, isDoctor),
            tableColumns: isDoctor ? this.getTableColumns(initColumnParams, "", isDoctor) : this.getTableColumns(initColumnParams, "room", isDoctor),
            toggleButtonInfos: this.getTableButtonInfos(props, isDoctor),
            appointmentLoadingState: LOADING_STATE.LOADING,
            hourSplit: AppUtils.getHourSplitOfDay(),
            daysBracket: this.getInitDaysBracket(props, isDoctor),
            doctorAppointmentsLoader: false
        };

        if (isDoctor) this.fetchDoctorAppointments();
        else this.fetchCenterAppointments();
    };

    componentDidMount() {
        let myInstance = this;
        myInstance.props.clearOngoingConsultation();
        this.intervalFunction = setInterval(function () {
            myInstance.fetchDoctorAppointments(true)
        }, 30000);
    }

    componentWillUnmount() {
        clearInterval(this.intervalFunction);
    }

    getTableButtonInfos(props: DashboardPageProps, isDoctor?: boolean) {
        if (isDoctor) {
            return [{ display: "Week", meta: 7 }, { display: "Day", meta: 1 }]
        } else {
            let roomsCount = props.centerInfo.rooms.length;
            let startRoom = props.centerInfo.rooms[0];
            let doctorsCount = props.centerInfo.doctors.length;
            let startDoctor = props.centerInfo.doctors[0];
            return [{ display: "Rooms", meta: { startColumn: startRoom, numOfColumns: roomsCount, view: "room" } }, { display: "Doctors", meta: { startColumn: startDoctor, numOfColumns: doctorsCount, view: "doctor" } }]
        }
    }

    getInitTableParams(props: DashboardPageProps, isDoctor?: boolean) {
        if (isDoctor) {
            return {
                startColumn: AppUtils.getTodaysEpoch().startEpoch,
                numOfColumns: AppConstants.DAYS_IN_A_WEEK
            }
        } else {
            return {
                startColumn: props.centerInfo.rooms[0].id,
                numOfColumns: props.centerInfo.rooms.length
            }
        }
    }

    getTableColumns = (initTableParams: ITableParams, selectedDashboardView?: string, isDoctorFlag?: boolean) => {
        let isDoctor: boolean = false;
        if (isDoctorFlag !== undefined) {
            isDoctor = isDoctorFlag;
        } else {
            if (this.state.view === "doctor") isDoctor = true;
        }
        if (isDoctor) {
            return AppUtils.getListOfDays(initTableParams.startColumn, initTableParams.startColumn + initTableParams.numOfColumns * EpochConstants.EPOCH_IN_AN_DAY)
        } else {
            if (selectedDashboardView === "room") {
                return this.props.centerInfo.rooms;
            } else {
                return this.props.centerInfo.doctors;
            }
        }
    }

    getInitDaysBracket(props: DashboardPageProps, isDoctor?: boolean) {
        return {
            startTimeEpoch: AppUtils.getTodaysEpoch().startEpoch,
            numberOfDays: isDoctor ? AppConstants.DAYS_IN_A_WEEK : 1
        }
    }

    fetchDoctorAppointments = (isCallback?: boolean) => {
        const { doctorProfile } = this.props;
        let myInstance = this;
        if (isCallback === undefined || !isCallback) {
            this.setState({
                doctorAppointmentsLoader: true
            })
        }

        this.props.fetchDoctorAppointments(doctorProfile.id,
            this.state.tableParams.startColumn, this.state.tableParams.numOfColumns, undefined, undefined, ConsultationUtils.isPackInfoRequired(doctorProfile.type)).then(() => {
                !isCallback && AppUtils.updatePageLoadEvent(EventNames.Calendar, "", this.loadStartTime)
                myInstance.setState({
                    doctorAppointmentsLoader: false
                })
            });
    }

    fetchCenterAppointments() {
        const { doctorProfile, centerInfo } = this.props;
        this.props.fetchCenterAppointments(centerInfo.center.id, this.state.daysBracket.startTimeEpoch, this.state.daysBracket.numberOfDays, ConsultationUtils.isPackInfoRequired(doctorProfile.type)).then(() => {
            AppUtils.updatePageLoadEvent(EventNames.Calendar, "", this.loadStartTime)
        });
    }

    updateTableNumOfColumns(meta: any) {
        this.updateTableParams(null, meta.numOfColumns);
    }

    updateTableStartColumn(meta: any) {
        this.updateTableParams(meta.startColumn, null);
    }

    updateTableStartColumnAndNumOfColumns(meta: any) {
        this.updateTableParams(meta.startColumn, meta.numOfColumns, meta.view);
    }

    toggleDayWeek = (meta: any): void => {
        this.updateTableNumOfColumns({ numOfColumns: meta });
    }

    toggleRoomDoctor = (meta: any): void => {
        this.updateTableStartColumnAndNumOfColumns(meta);
    }

    navigateDate = (forward: boolean) => {
        if (!this.state.doctorAppointmentsLoader) {
            if (this.state.view === "doctor") {
                let newStartEpoch = this.state.tableParams.startColumn;
                let epochDiff = this.state.tableParams.numOfColumns * EpochConstants.EPOCH_IN_AN_DAY;
                newStartEpoch = forward ? newStartEpoch + epochDiff : newStartEpoch - epochDiff;
                this.updateTableStartColumn({ startColumn: newStartEpoch });
            } else {
                let newStartEpoch = this.state.daysBracket.startTimeEpoch;
                let epochDiff = this.state.daysBracket.numberOfDays * EpochConstants.EPOCH_IN_AN_DAY;
                newStartEpoch = forward ? newStartEpoch + epochDiff : newStartEpoch - epochDiff;
                this.updateDaysBracket(newStartEpoch);
            }
        }
    }

    updateTableParams(newStartColumn: number, newNumOfColumns: number, view?: string) {
        let newDaysBracket: IDaysBracket = Object.assign({}, this.state.daysBracket);
        if (this.state.view === "doctor") {
            newDaysBracket.startTimeEpoch = newStartColumn ? newStartColumn : newDaysBracket.startTimeEpoch;
            newDaysBracket.numberOfDays = newNumOfColumns ? newNumOfColumns : newDaysBracket.numberOfDays;
        }

        let newTableParams = Object.assign({}, this.state.tableParams);
        newTableParams.startColumn = newStartColumn ? newStartColumn : newTableParams.startColumn;
        newTableParams.numOfColumns = newNumOfColumns ? newNumOfColumns : newTableParams.numOfColumns;

        let tableColumns = view !== undefined ? this.getTableColumns(newTableParams, view) : this.getTableColumns(newTableParams);

        let selectedDashboardView = view !== undefined ? view : this.state.selectedDashboardView;

        this.setState({ daysBracket: newDaysBracket, selectedDashboardView, tableParams: newTableParams, tableColumns, uiParams: { selectedColumnIdx: 0 }, appointmentLoadingState: LOADING_STATE.NOT_INITIALIZED }, () => {
            if (this.state.view === "doctor") this.fetchDoctorAppointments();
            else this.fetchCenterAppointments();
        });
    }

    updateDaysBracket(newStartTimeEpoch: number, newNumberOfDays?: number) {
        let newDaysBracket: IDaysBracket = Object.assign({}, this.state.daysBracket);
        newDaysBracket.startTimeEpoch = newStartTimeEpoch ? newStartTimeEpoch : newDaysBracket.startTimeEpoch;
        newDaysBracket.numberOfDays = newNumberOfDays ? newNumberOfDays : newDaysBracket.numberOfDays;

        this.setState({ daysBracket: newDaysBracket }, () => {
            if (this.state.view === "doctor") this.fetchDoctorAppointments();
            else this.fetchCenterAppointments();
        })
    }

    getAppointmentsToShow(appointments: IAppointmentScheduleResponse[]) {
        let appointmentsToShow: IAppointmentScheduleResponse[] = null;
        if (!_.isNull(appointments)) {
            appointmentsToShow = appointments.filter(item => item.status !== ConsultationStatusConstant.WAITING_FOR_CONFIRMATION &&
                item.status !== ConsultationStatusConstant.CANCELLED &&
                item.status !== ConsultationStatusConstant.BOOKED);
        }
        return appointmentsToShow;
    }

    getDateRangeToShow = (totCalendDays: number) => {
        let startDate: string = this.state.tableColumns[0].dateText + " " + this.state.tableColumns[0].month;
        let endDate: string = this.state.tableColumns[totCalendDays - 1].dateText + " " + this.state.tableColumns[totCalendDays - 1].month;
        if (startDate !== endDate) {
            return (<React.Fragment>
                <span className="date-nav-text date-text-left">{startDate}</span>
                <span className="date-nav-sep"> - </span>
                <span className="date-nav-text date-text-right">{endDate}</span>
            </React.Fragment>)
        }

        return (<React.Fragment>
            <span className="date-nav-text date-text-right">{endDate}</span>
        </React.Fragment>)

    }

    render() {
        let myInstance = this;
        let totCalendDays = this.state.daysBracket.numberOfDays;
        let numOfAppointments = this.props.appointments ? this.getAppointmentsToShow(this.props.appointments).length : 0;
        let centerAppointments = this.props.centerInfo.appointments ? this.props.centerInfo.appointments.length : 0;
        let appointmentsToPass = this.state.view === "doctor" ? this.getAppointmentsToShow(this.props.appointments) : this.props.centerInfo.appointments;
        let startTimeToDisplay = AppUtils.getDayEpochInfo(this.state.daysBracket.startTimeEpoch).dateText + " " + AppUtils.getDayEpochInfo(this.state.daysBracket.startTimeEpoch).month;
        let endTimeToDisplay = (this.state.daysBracket.numberOfDays !== 1 ? AppUtils.getDayEpochInfo(this.state.daysBracket.startTimeEpoch + EpochConstants.EPOCH_IN_AN_DAY).dateText + " " + AppUtils.getDayEpochInfo(this.state.daysBracket.startTimeEpoch + EpochConstants.EPOCH_IN_AN_DAY).month : "");
        const isMobile = AppUtils.getIsMobile();
        return (
            <div className="doctor-dashboard">
                <div className="dashboard-top-nav">
                    {
                        <div className="day-week-selector">
                            <ToggleButtonGroup toggleButtonInfos={this.state.toggleButtonInfos} startSelIdx={0} onToggleButtonClick={myInstance.state.view === "doctor" ? this.toggleDayWeek : this.toggleRoomDoctor} />
                        </div>
                    }
                    <div className="date-nav">
                        <img className="date-navigator date-left-nav" src={ORION_IMG_URL + "date-left-nav.png"} onClick={() => this.navigateDate(false)} />
                        {
                            this.state.view === "nurse" &&
                            <div className="date-nav-display">
                                <div className="date-nav-date-display brandon-bold">
                                    <span className="date-nav-text date-text-left">{startTimeToDisplay}</span>
                                    {
                                        this.state.daysBracket.numberOfDays !== 1 &&
                                        <span>
                                            <span className="date-nav-sep"> - </span>
                                            <span className="date-nav-text date-text-right">{endTimeToDisplay}</span>
                                        </span>
                                    }
                                </div>
                                <div className="date-nav-appt-display">
                                    {centerAppointments} Appointments
                                </div>
                            </div>
                        }
                        {
                            this.state.view === "doctor" &&
                            <div className="date-nav-display">
                                <div className="date-nav-date-display brandon-bold">
                                    {this.getDateRangeToShow(totCalendDays)}
                                </div>
                                {
                                    !this.state.doctorAppointmentsLoader &&
                                    <div className="date-nav-appt-display">
                                        {numOfAppointments} Appointments
                                    </div>
                                }
                                {
                                    this.state.doctorAppointmentsLoader &&
                                    <div className="date-nav-appt-display">
                                        ...
                                    </div>
                                }
                            </div>
                        }
                        <img className="date-navigator date-right-nav" src={ORION_IMG_URL + "date-right-nav.png"} onClick={() => this.navigateDate(true)} />
                    </div>
                    {!isMobile && <div className={"top-nav-right-col"}>
                        <ShareProfile doctorProfile={this.props.doctorProfile} />
                        <div className={"glossary"}>
                            <div><span className={"empty-block tc"}></span>Tele-consultation</div>
                            <div><span className={"empty-block"}></span>Incentre</div>
                            <div><span className={"empty-block missed"}></span>Missed</div>
                        </div>
                    </div>}
                </div>
                {
                    !_.isNull(appointmentsToPass) &&
                    <DashboardCalendarComponent appointmentLoadingStatus={this.state.doctorAppointmentsLoader} view={myInstance.state.view} selectedDashboardView={myInstance.state.selectedDashboardView} calendarColumns={myInstance.state.tableColumns} appointments={appointmentsToPass} hourSplit={myInstance.state.hourSplit} daysBracket={this.state.daysBracket} fetchAppointments={myInstance.fetchDoctorAppointments} />
                }
                {isMobile && <div className={"glossary-mobile-bar"}>
                    <div><span className={"empty-block tc"}></span>Tele-consultation</div>
                    <div><span className={"empty-block"}></span>Incentre</div>
                    <div><span className={"empty-block missed"}></span>Missed</div>
                </div>}
            </div>
        )
    }
}

function mapStateToProps(state: IAppState): any {
    return {
        accountData: state.accountData,
        doctorProfile: state.doctorProfile,
        appointments: state.appointments,
        centerInfo: state.centerInfo
    };
}

const mapDispatchToProps = (dispatch: any) => {
    return {
        fetchDoctorAppointments: (doctorId: number, startTimeEpoch: number, numOfDays: number, isForPoll?: boolean, tillTimeEpoch?: number, isPackInfoRequired?: boolean) => {
            return AppointmentsActions.fetchDoctorAppointment(dispatch, doctorId, startTimeEpoch, numOfDays, isForPoll, tillTimeEpoch, isPackInfoRequired)
        },
        fetchCenterAppointments: (centerId: number, startTimeEpoch: number, numOfDays: number, isPackInfoRequired?: boolean) => {
            return AppointmentsActions.fetchCenterAppointments(dispatch, centerId, startTimeEpoch, numOfDays, isPackInfoRequired);
        },
        clearOngoingConsultation: () => {
            return dispatch(OngoingConsultationActions.clearOngoingConsultationDetails());
        }
    }
};

export default connect(mapStateToProps, mapDispatchToProps)(DashboardPage);

